import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalog/ad/catalogAd.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { fetchAllCategoriesLandingPageData, fetchCategoriesIfNeeded } from '@/redux/modules/category/category.actions';
import { fetchCatalogIds } from '@/redux/modules/categoryCatalogCards';
import { fetchCategoryRelatedSearches } from '@/redux/modules/categoryRelatedSearches';
import {
    fetchCombinationRelatedSearches,
    fetchCombinationSuggestedCategories,
} from '@/redux/modules/combinationCategory';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { searchCategory } from '@/redux/modules/landingPage';
import { fetchCategoryRelatedSearchTiles } from '@/redux/modules/search/categoryRelatedSearches/categoryRelatedSearches.actions';
import { submitSearch } from '@/redux/modules/search/search.actions';
import { fetchSubcategories } from '@/redux/modules/subcategoryTiles';
import { fetchSuggestedCategories } from '@/redux/modules/suggestedCategoryTiles';
import { SearchPageName, SearchQuery } from '@/types/search/searchQuery';
import { getCombinationCategoryIdsFromPath, isSimilarItemsPage } from '@/utils/urls';

type Dispatch = (action: any) => Promise<any>;

type RunPreloadPhaseArgs = {
    dispatch: Dispatch;
    isCombinationCategory: boolean;
    itemId: number;
    legacyCategoryId2: number;
    params: Record<string, any>;
    pathname: string;
    queryParams: Record<string, any>;
};

export const runPreloadPhase = ({
    dispatch,
    isCombinationCategory,
    itemId,
    legacyCategoryId2,
    params,
    pathname,
    queryParams,
}: RunPreloadPhaseArgs) => {
    const promises: Promise<any>[] = [];
    let combinationIds: string | undefined;

    if (itemId && isSimilarItemsPage(pathname)) {
        promises.push(
            dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'SIMILAR-ITEMS-CATEGORY-SEARCH-RESULTS',
                    itemIds: [itemId],
                })
            )
        );
    }

    if (params?.categoryId2 && !Number.isNaN(legacyCategoryId2)) {
        combinationIds = getCombinationCategoryIdsFromPath(params, queryParams);
        if (isCombinationCategory && combinationIds) {
            promises.push(dispatch(fetchCombinationRelatedSearches(combinationIds)));
            promises.push(dispatch(fetchCombinationSuggestedCategories(combinationIds)));
        }
    }

    if (pathname === '/c/' || pathname === '/c') {
        promises.push(dispatch(fetchAllCategoriesLandingPageData()));
    }

    return Promise.all(promises).then(() => ({ combinationIds }));
};

type RunSecondaryPhaseArgs = {
    adsCategoryKey: string;
    dispatch: Dispatch;
    finalCategoryIdForTiles: number;
    itemId: number;
    keywords: string;
    pageName: string;
    pathname: string;
    relatedSearchTilesParams: { categoryIds: number[]; concatenatedFacetIds: string[] | undefined };
    searchTerm: string;
    shouldFetchCategoryBanner: boolean;
};

export const runSecondaryPhase = async ({
    adsCategoryKey,
    dispatch,
    finalCategoryIdForTiles,
    itemId,
    keywords,
    pageName,
    pathname,
    relatedSearchTilesParams,
    searchTerm,
    shouldFetchCategoryBanner,
}: RunSecondaryPhaseArgs) => {
    const secondaryPromises: Promise<any>[] = [
        dispatch(
            fetchCatalogAdIfNeeded({
                categoryIds: adsCategoryKey,
                keywords: `${keywords}`,
                pageName,
            })
        ),
        dispatch(fetchSubcategories(String(finalCategoryIdForTiles))),
        dispatch(fetchCategoryRelatedSearches([itemId], String(finalCategoryIdForTiles))),
        dispatch(fetchCategoryRelatedSearchTiles(relatedSearchTilesParams)),
        dispatch(fetchSuggestedCategories(String(finalCategoryIdForTiles))),
        dispatch(fetchCatalogIds({ searchTerm })),
    ];

    if (shouldFetchCategoryBanner || /\/c\/[\w-]+\/\d+/.test(pathname)) {
        secondaryPromises.push(
            dispatch(
                fetchCatalogAdIfNeeded({
                    categoryIds: adsCategoryKey,
                    force: true,
                    keywords: `${keywords}`,
                    pageName: 'category-banner',
                })
            )
        );
    }

    await Promise.all(secondaryPromises);
};

type RunSearchPhaseArgs = {
    catalogIds: number[];
    dispatch: Dispatch;
    landingPageIds: number[];
    needsAggs: boolean;
    searchQuery: SearchQuery;
    /**
     * When true, dispatch the generic keyword `submitSearch` (v4/web) instead of `searchCategory`
     * (v4/categoryweb). Required when we have no resolvable category ids — categoryweb rejects
     * empty `ids` arrays and we end up on the SearchError page.
     */
    useKeywordSearchFallback?: boolean;
};

export const runSearchPhase = async ({
    catalogIds,
    dispatch,
    landingPageIds,
    needsAggs,
    searchQuery,
    useKeywordSearchFallback = false,
}: RunSearchPhaseArgs) =>
    Promise.all([
        dispatch(fetchCatalogsIfNeeded({ catalogIds })),
        useKeywordSearchFallback
            ? dispatch(
                  submitSearch({
                      pageName: SearchPageName.SEARCH,
                      searchQuery,
                      skipAggs: !needsAggs,
                  })
              )
            : dispatch(
                  searchCategory({
                      ids: landingPageIds,
                      pageName: null,
                      searchQuery,
                      skipAggs: !needsAggs,
                  })
              ),
        dispatch(fetchCategoriesIfNeeded({ categoryIds: landingPageIds })),
    ]);
